import { Box, Column, Row, Switch, Typography } from '@tempmee/react';
import { type Control, useController } from 'react-hook-form';
import { SmartTooltip } from '~/commonComponents';
import InfoIcon from '~/images/icons/Info';
import {
  fireShiftRequestAutoconfirmFavoriteSelected,
  fireShiftRequestAutoconfirmEligibleSelected,
  fireEditShiftAutoconfirmFavoriteSelected,
  fireEditShiftAutoconfirmEligibleSelected,
  fireOpenShiftAutoconfirmFavoriteSelected,
  fireOpenShiftAutoconfirmEligibleSelected,
} from '~/posthogs/events';
import { Colors } from '~/themes/colors';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';

export type AutoConfirmEventContext = 'shift_request' | 'edit_shift' | 'open_shift';

interface IAutoConfirmFieldProps {
  control: Control<IShiftDetailsFormValues>;
  professionType: string;
  preferredSpecialty: string;
  eventContext?: AutoConfirmEventContext;
  hideHeader?: boolean;
}

const TOOLTIP_CONTENT = (
  <Typography as="span" bold color="#FFFFFF">
    Professional&apos;s eligibility criteria:
    <br />
    <br />
    {'\u2022'} Has at least a 95% attendance score for previous shifts worked.
    <br />
    {'\u2022'} Has a minimum of 2 years working experience.
    <br />
    {'\u2022'} Has received exceptional performance ratings on over 95% of completed shifts.
    <br />
    {'\u2022'} Has completed at least 3 shifts.
  </Typography>
);

export const AutoConfirmField = ({
  control,
  professionType,
  preferredSpecialty,
  eventContext,
  hideHeader = false,
}: IAutoConfirmFieldProps) => {
  const { field } = useController({ name: 'autoConfirm', control });

  const getAutoFillInfo = (professionType: string, specialty: string) => {
    if (['Associate Dentist', 'Front Desk'].includes(professionType)) {
      return {
        show: false,
        message: `The Auto-Confirm feature is not available for ${professionType} at this time.`,
      };
    }

    if (!specialty?.toLowerCase()?.includes('general')) {
      return {
        show: false,
        message:
          'The Auto-Confirm feature is only available for shifts without a specialty preference (General).',
      };
    }

    return {
      show: true,
      message: (
        <Box>
          - Auto-Confirm does not apply to counter offers.
          <br />- Selecting “Favorites” will send them invites.
        </Box>
      ),
    };
  };

  const autoFillInfo = getAutoFillInfo(professionType, preferredSpecialty);

  if (!autoFillInfo?.show) {
    return (
      <Column
        data-testid="auto-confirm-field"
        data-cy="auto-confirm-field"
        style={{ gap: 12 }}
      >
        {!hideHeader && (
          <Row align="center" gap={2}>
            <Typography bold color={Colors.neutral_600} style={{ fontSize: 18 }}>
              Auto-Confirm:
            </Typography>
          </Row>
        )}
        <Typography
          italic
          color={Colors.neutral_500}
          data-testid="auto-confirm-unavailable-message"
          data-cy="auto-confirm-unavailable-message"
          style={{ fontSize: 16, padding: '0 24px' }}
        >
          The Auto-Confirm feature is only available for shifts without a specialty preference
          (General).
        </Typography>
      </Column>
    );
  }

  const fireToggleEvent = (key: 'favorites' | 'eligibleProfessionals', newValue: boolean) => {
    if (!eventContext) return;

    const payload = {
      autoConfirm: (newValue ? 'on' : 'off') as 'on' | 'off',
      designDate: 202603,
    };

    const eventMap = {
      shift_request: {
        favorites: fireShiftRequestAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireShiftRequestAutoconfirmEligibleSelected,
      },
      edit_shift: {
        favorites: fireEditShiftAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireEditShiftAutoconfirmEligibleSelected,
      },
      open_shift: {
        favorites: fireOpenShiftAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireOpenShiftAutoconfirmEligibleSelected,
      },
    };

    eventMap[eventContext][key](payload);
  };

  const handleToggle = (key: 'favorites' | 'eligibleProfessionals') => {
    const current = field.value ?? { favorites: false, eligibleProfessionals: false };
    const newValue = !current[key];
    field.onChange({
      ...current,
      [key]: newValue,
    });
    fireToggleEvent(key, newValue);
  };

  return (
    <Column
      data-testid="auto-confirm-field"
      data-cy="auto-confirm-field"
      style={{ padding: '0 24px', gap: 12 }}
    >
      {!hideHeader && (
        <Row align="center" gap={2} style={{ marginBottom: 8 }}>
          <Typography bold color={Colors.neutral_600} style={{ fontSize: 18 }}>
            Auto-Confirm:
          </Typography>
          <SmartTooltip position="bottom" content={TOOLTIP_CONTENT}>
            <InfoIcon color={Colors.neutral_300} />
          </SmartTooltip>
        </Row>
      )}

      <Switch
        checked={field.value?.favorites ?? false}
        onChange={() => handleToggle('favorites')}
        variant="favorites"
        label={
          <Typography color={Colors.neutral_600} style={{ fontSize: 16 }}>
            Favorites
          </Typography>
        }
        data-testid="favorites-toggle"
        data-cy="favorites-toggle"
      />

      <Switch
        checked={field.value?.eligibleProfessionals ?? false}
        onChange={() => handleToggle('eligibleProfessionals')}
        label={
          <Typography color={Colors.neutral_600} style={{ fontSize: 16 }}>
            Eligible Professionals
          </Typography>
        }
        data-testid="eligible-toggle"
        data-cy="eligible-toggle"
      />

      <Box
        data-testid="auto-confirm-message"
        data-cy="auto-confirm-message"
        style={{ marginTop: 8 }}
      >
        <Typography color={Colors.neutral_500} style={{ fontSize: 16 }}>
          {autoFillInfo.message}
        </Typography>
      </Box>
    </Column>
  );
};
